import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BrandLogo } from "../layout/BrandLogo";
import { prefersReducedMotion } from "../../lib/deviceCapability";

const SESSION_KEY = "aurelia:intro-shown";
const HOLD_MS = 2200;

export function IntroSplash() {
  const [visible, setVisible] = useState(false);

  // Claiming "already shown this session" has no cleanup, so it stays correct
  // even under StrictMode's dev-only double-invoke of effects. Scheduling the
  // dismissal timer is split into its own effect keyed on `visible` so its
  // setTimeout/clearTimeout pairing is what StrictMode double-invokes instead —
  // otherwise the second invoke would see the flag already set by the first
  // and bail out before ever scheduling the real timer.
  useEffect(() => {
    if (prefersReducedMotion() || sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setVisible(false), HOLD_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => setVisible(false)}
          className="fixed inset-0 z-[100] bg-[#050813] flex flex-col items-center justify-center gap-5 cursor-pointer overflow-hidden"
          aria-hidden="true"
        >
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 50% at 50% 55%, rgba(220,182,98,0.28), transparent 70%)," +
                "radial-gradient(ellipse 40% 40% at 75% 25%, rgba(92,124,250,0.22), transparent 70%)," +
                "radial-gradient(ellipse 40% 40% at 20% 30%, rgba(92,124,250,0.15), transparent 70%)"
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <BrandLogo variant="full" forceTheme="dark" className="h-14 md:h-20 w-auto" />
          </motion.div>
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative text-xs md:text-sm font-medium text-[#f6f1e7]/70 tracking-[0.3em] uppercase"
          >
            Considered essentials, made to last
          </motion.span>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-px w-16 bg-[#dcb662] origin-center"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
